(function () {
  "use strict";

  if (customElements.get("parallax-element")) return;

  customElements.define(
    "parallax-element",
    class extends HTMLElement {
      constructor() {
        super();

        this.container = this.parentNode;

        this.percentage = 0;
        this.percentageFull = 0;

        this.animation = this.getAttribute("data-parallax-animation");
        this.animations = this.animation.split(",");

        this.disableOnMobile = this.hasAttribute(
          "data-parallax-disable-on-mobile"
        );

        this.viewportHeight = Math.max(
          document.documentElement.clientHeight,
          window.innerHeight || 0
        );

        this.containerHeight = this.container.offsetHeight;

        this.main = document.querySelector("[data-main]");
        this.firstSection = this.main.children[0];

        this.orientation = window.theme.getScreenOrientation();

        // Bind events
        this.scrollEvent = () => this.updateParallax();
        this.resizeEvent = () => this.updateHeight();
        this.refreshEvent = () => this.refresh();
      }

      /* ========================
         FOOTER SETUP
      ======================== */
      assignFooterArguments() {
        if (!this.animations.includes("footer")) return;

        this.shopifySection = this.closest(".shopify-section");

        this.footerPrevSectionOffset = 0;

        if (this.hasAttribute("data-parallax-rounded-corners")) {
          this.footerPrevSectionOffset = 8;

          if (
            this.hasAttribute("data-parallax-rounded-corners-large")
          ) {
            this.footerPrevSectionOffset = 34;
          }
        }

        this.prevSection =
          this.container.parentNode.previousElementSibling;

        if (
          !this.prevSection ||
          this.prevSection.classList.contains("shopify-section") ||
          this.prevSection.classList.contains("main-content")
        ) {
          this.prevSection = this.main;
        }

        this.containerHeight = this.container.offsetHeight;

        this.footerOffset = 0.15 * this.containerHeight;

        this.cachedPosition = this.footerOffset;
        this.cachedPercentage = 0;
      }

      /* ========================
         CARD SCROLL SETUP
      ======================== */
      assignCardScrollingArguments() {
        if (!this.animations.includes("card-scrolling")) return;

        const { stickyHeaderHeight } =
          window.theme.readHeights();

        this.headerHeight = stickyHeaderHeight || 0;

        this.shopifySection = this.closest(".shopify-section");
        this.nextSection = this.shopifySection.nextElementSibling;

        this.cardScrollingStickyTop = this.headerHeight;

        this.cardScrollingMarginTop = this.nextSection
          ? parseInt(
              window
                .getComputedStyle(this.nextSection)
                .getPropertyValue("margin-top")
            )
          : 0;

        this.after = false;

        this.prevSection =
          this.shopifySection.previousElementSibling;

        if (this.prevSection) {
          const firstSectionId = this.firstSection.id;
          const isSameAsFirst =
            this.prevSection.matches(`#${firstSectionId}`);

          const prevHeight = this.prevSection.offsetHeight;

          this.shopifySection.classList.toggle(
            "sticky-top-zero",
            isSameAsFirst &&
              prevHeight <= 2 * this.headerHeight
          );
        }

        // EXACT same logic as original
        this.opacityValue = (value) =>
          value
            ? value <= 0
              ? 0
              : value > 0 && value <= 100
              ? Number(value / 100).toFixed(4)
              : value > 100
              ? 1
              : undefined
            : 0;
      }

      /* ========================
         LIFECYCLE
      ======================== */
      connectedCallback() {
        this.assignFooterArguments();
        this.assignCardScrollingArguments();

        requestAnimationFrame(() => {
          this.updateParallax();

          document.addEventListener(
            "theme:scroll",
            this.scrollEvent
          );
          document.addEventListener(
            "theme:resize",
            this.resizeEvent
          );
          document.addEventListener(
            "theme:parallax:refresh",
            this.refreshEvent
          );
        });
      }

      disconnectedCallback() {
        document.removeEventListener(
          "theme:scroll",
          this.scrollEvent
        );
        document.removeEventListener(
          "theme:resize",
          this.resizeEvent
        );
        document.removeEventListener(
          "theme:parallax:refresh",
          this.refreshEvent
        );
      }

      refresh() {
        this.assignFooterArguments();
        this.assignCardScrollingArguments();

        requestAnimationFrame(() =>
          this.updateParallax()
        );
      }

      /* ========================
         MAIN LOOP
      ======================== */
      updateParallax() {
        if (
          this.container.classList.contains("is-disabled") ||
          (this.disableOnMobile &&
            window.theme.isMobile())
        ) {
          return;
        }

        this.scrollTop = Math.round(window.scrollY);
        this.scrollBottom =
          this.scrollTop + this.viewportHeight;

        this.elementOffsetTopPoint =
          Math.round(
            this.container.getBoundingClientRect().top +
              this.scrollTop
          );

        if (window.Shopify.designMode) {
          this.assignCardScrollingArguments();
          this.containerHeight =
            this.container.offsetHeight;
        }

        this.elementOffsetBottomPoint =
          this.elementOffsetTopPoint +
          this.containerHeight;

        this.isBottomOfElementPassed =
          this.elementOffsetBottomPoint < this.scrollTop;

        this.isTopOfElementReached =
          this.elementOffsetTopPoint <
          this.scrollBottom;

        this.isInView =
          this.isTopOfElementReached &&
          !this.isBottomOfElementPassed;

        this.adjustCalculations();
        this.toggleVisibility();

        if (!this.isInView) return;

        this.scrollProgress =
          this.scrollBottom -
          this.elementOffsetTopPoint;

        this.percentage =
          (100 *
            (this.scrollProgress -
              this.containerHeight / 2)) /
          this.viewportHeight;

        this.percentageFull = Number(
          this.scrollProgress / this.containerHeight
        ).toFixed(2);

        this.percentageVisible = Number(
          (100 * this.scrollProgress) /
            (this.viewportHeight +
              this.containerHeight)
        ).toFixed(2);

        if (this.animations.includes("circle")) {
          this.animateCircleText();
        }

        if (
          this.animations.includes("horizontal") ||
          this.animations.includes("vertical") ||
          this.animations.includes("diagonal")
        ) {
          this.animateOverlappingImages();
        }

        if (
          this.animations.includes("offset-ltr") ||
          this.animations.includes("offset-rtl")
        ) {
          this.offsetX();
        }

        if (this.animations.includes("card-scrolling")) {
          this.cardScrollingEffect();
        }

        if (this.animations.includes("zoom-on-scroll")) {
          this.zoomInOut();
        }

        if (this.animations.includes("footer")) {
          this.animateFooter();
        }
      }

      /* ========================
         ADJUSTMENTS
      ======================== */
      adjustCalculations() {
        if (this.animations.includes("card-scrolling")) {
          this.currentSectionTop =
            this.shopifySection.getBoundingClientRect().top;

          if (this.nextSection) {
            this.nextSectionTop =
              this.nextSection.getBoundingClientRect().top;

            this.sectionOffsetTop =
              this.nextSectionTop -
              this.containerHeight -
              this.cardScrollingMarginTop;
          } else {
            this.nextSectionTop =
              this.currentSectionTop +
              this.containerHeight;

            this.sectionOffsetTop =
              this.currentSectionTop;
          }

          this.distance = Math.floor(
            this.currentSectionTop - this.headerHeight
          );

          this.after = Boolean(
            Math.round(
              this.nextSectionTop -
                this.headerHeight -
                this.cardScrollingMarginTop +
                0.1 * this.viewportHeight
            ) < 0
          );

          this.isInView =
            this.isTopOfElementReached && !this.after;
        }

        if (this.animations.includes("footer")) {
          if (window.Shopify.designMode) {
            this.assignFooterArguments();
          }

          this.elementOffsetTopPoint =
            Math.round(
              this.prevSection?.getBoundingClientRect()
                .bottom -
                this.footerPrevSectionOffset +
                this.scrollTop
            );

          this.elementOffsetBottomPoint =
            this.elementOffsetTopPoint +
            this.containerHeight;

          this.isBottomOfElementPassed =
            this.elementOffsetBottomPoint <
            this.scrollTop;

          this.isTopOfElementReached =
            this.elementOffsetTopPoint <
            this.scrollBottom;

          this.isInView =
            this.isTopOfElementReached &&
            !this.isBottomOfElementPassed;

          this.isApproaching =
            Math.round(
              this.elementOffsetTopPoint -
                this.viewportHeight / 2
            ) < this.scrollBottom;
        }
      }

      toggleVisibility() {
        if (this.animations.includes("card-scrolling")) {
          this.shopifySection.classList.toggle(
            "card-scrolling-hidden",
            this.after
          );
        }

        if (this.animations.includes("footer")) {
          if (this.isApproaching || this.isInView) {
            this.shopifySection.classList.add(
              "section-footer--visible"
            );
          } else {
            this.shopifySection.classList.remove(
              "section-footer--visible"
            );
          }
        }
      }

      /* ========================
         EFFECTS (UNCHANGED LOGIC)
      ======================== */

      cardScrollingEffect() {
        const value =
          (this.sectionOffsetTop /
            this.containerHeight) *
          -100;

        this.container.style.setProperty(
          "--card-scrolling-overlay",
          this.opacityValue(value)
        );
      }

      zoomInOut() {
        if (
          this.animations.includes("card-scrolling") &&
          this.nextSection
        ) {
          const offsetTop =
            this.nextSectionTop -
            this.headerHeight -
            this.cardScrollingMarginTop -
            this.containerHeight;

          const elementPosition = Math.round(
            offsetTop + this.scrollTop
          );

          const visibleDistance =
            this.scrollBottom - elementPosition;

          this.percentageVisible = Number(
            (100 * visibleDistance) /
              (this.viewportHeight +
                this.containerHeight +
                this.cardScrollingMarginTop)
          ).toFixed(2);
        }

        let normalizedProgress = this.percentageVisible;
        if (
          this.container.classList.contains('slider-modified') ||
          this.container.tagName.toLowerCase() === 'banner-image-component' ||
          this.container.classList.contains('section-image-modified') ||
          this.container.classList.contains('featured-video-section-modified')
        ) {
          // Capture starting point ONCE
          if (!this.initialPercentageVisible) {
            this.initialPercentageVisible = this.percentageVisible;
          }

          // Normalize progress (start from 0)
          normalizedProgress =
            this.percentageVisible - this.initialPercentageVisible;
        }

        const scaleAmount =
          (0.2 * normalizedProgress) / 100;

        let scale = Number(1 + scaleAmount).toFixed(4);

        if (scale < 1) scale = 1;

        this.container.style.setProperty(
          "--scale",
          scale
        );
      }

      offsetX() {
        const isStatic = this.hasAttribute(
          "data-parallax-static"
        );

        const intensity =
          this.getAttribute(
            "data-parallax-intensity"
          ) || 100;

        let baseOffset = this.animations.includes(
          "offset-ltr"
        )
          ? intensity
          : 0;

        if (isStatic) baseOffset = intensity / 2;

        const direction = this.animations.includes(
          "offset-ltr"
        )
          ? 1
          : -1;

        const movement =
          (intensity * this.percentageVisible) / 100 -
          baseOffset;

        const finalOffset = Number(
          movement * direction
        ).toFixed(2);

        this.container.style.setProperty(
          "--offsetX",
          `${finalOffset}%`
        );
      }

      updateHeight() {
        if (this.animations.includes("card-scrolling")) {
          const { stickyHeaderHeight } =
            window.theme.readHeights();

          this.headerHeight = stickyHeaderHeight || 0;
          this.cardScrollingStickyTop = this.headerHeight;

          this.cardScrollingMarginTop = this.nextSection
            ? parseInt(
                window
                  .getComputedStyle(this.nextSection)
                  .getPropertyValue("margin-top")
              )
            : 0;

          this.containerHeight =
            this.container.offsetHeight;

          this.prevSection =
            this.shopifySection.previousElementSibling;

          if (this.prevSection) {
            const firstSectionId = this.firstSection.id;

            const isSame =
              this.prevSection.matches(
                `#${firstSectionId}`
              );

            const prevHeight =
              this.prevSection.offsetHeight;

            this.shopifySection.classList.toggle(
              "sticky-top-zero",
              isSame &&
                prevHeight <= 2 * this.headerHeight
            );
          }
        }

        const isDesktop = matchMedia(
          "(min-width: 1024px)"
        ).matches;

        if (
          this.orientation !==
            window.theme.getScreenOrientation() ||
          isDesktop
        ) {
          this.viewportHeight = Math.round(
            Math.max(
              document.documentElement.clientHeight,
              window.innerHeight || 0
            )
          );

          this.containerHeight =
            this.container.offsetHeight;

          this.orientation =
            window.theme.getScreenOrientation();
        }
      }

      animateCircleText() {
        const rotation =
          (70 * this.percentage) / 100 * -1;

        if (this.percentage > 0) {
          this.container.style.setProperty(
            "--rotate",
            `${35 + rotation}deg`
          );
        }
      }

      animateOverlappingImages() {
        const intensity =
          0.25 *
          this.getAttribute("data-parallax-intensity");

        const half = intensity / 2;

        const isSingle = this.hasAttribute(
          "data-parallax-single"
        );

        const progress =
          (intensity * this.percentage) / 100;

        let primaryX = 0;
        let primaryY = 0;
        let secondaryX = 0;
        let secondaryY = 0;

        if (
          this.animations.includes("horizontal") ||
          this.animations.includes("diagonal")
        ) {
          primaryX = -half + progress;
          secondaryX = half - progress;
        }

        if (
          this.animations.includes("vertical") ||
          this.animations.includes("diagonal")
        ) {
          primaryY = half - progress;
          secondaryY = -half + progress;
        }

        this.container.style.setProperty(
          "--transformX-primary",
          `${primaryX}%`
        );
        this.container.style.setProperty(
          "--transformY-primary",
          `${primaryY}%`
        );

        if (!isSingle) {
          this.container.style.setProperty(
            "--transformX-secondary",
            `${secondaryX}%`
          );
          this.container.style.setProperty(
            "--transformY-secondary",
            `${secondaryY}%`
          );
        }
      }

      animateFooter() {
        const translateAmount =
          this.footerOffset * this.percentageFull;

        const percentage = Number(
          (translateAmount / this.footerOffset) * 100
        ).toFixed(2);

        const matrix = new WebKitCSSMatrix(
          window.getComputedStyle(this.container)
            .transform
        );

        const currentY = Number(matrix.m42).toFixed(2);

        let newY = Number(
          this.footerOffset - translateAmount
        ).toFixed(2);

        if (this.percentageFull >= 1) {
          newY = 0;
        }

        if (
          currentY !== newY &&
          this.cachedPercentage !== percentage
        ) {
          this.container.style.setProperty(
            "--transformY",
            `${newY}`
          );

          this.cachedPercentage = percentage;
          this.cachedPosition = newY;
        }
      }
    }
  );

})();

